import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { EASE } from '../components/motion.tsx';
import { Button, Callout, Chip, Dialog, Empty, Field, PageHead, Skeleton, Switch, useToast } from '../components/ui.tsx';
import { api, ApiError, get, patch, post, qs } from '../lib/api.ts';
import { addDays, date, todayIn } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';

interface RosterData {
  dates: string[];
  people: { id: string; name: string; employeeNo: string | null; jobPosition: string | null }[];
  entries: { employeeId: string; date: string; kind: string; shiftId: string | null; code: string | null; color: string | null }[];
}
interface Shift {
  id: string;
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  breakStart: string | null;
  breakEnd: string | null;
  color: string;
  overnight: boolean;
  crossDayClockIn: boolean;
  isDefault: boolean;
  officeId: string | null;
}

const mondayOf = (iso: string) => addDays(iso, -((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7));

export function Roster() {
  const me = useMe().data!;
  const lookups = useLookups();
  const qc = useQueryClient();
  const toast = useToast();
  const [start, setStart] = useState(() => mondayOf(todayIn(me.employee?.timeZone ?? undefined)));
  const [officeId, setOfficeId] = useState(me.employee?.officeId ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);
  const [pick, setPick] = useState('');
  const end = addDays(start, 13);
  const q = useQuery({ queryKey: ['roster', start, officeId], queryFn: () => get<RosterData>(`/api/attendance/roster${qs({ from: start, to: end, officeId })}`) });
  const shifts = useQuery({ queryKey: ['shifts'], queryFn: () => get<Shift[]>('/api/attendance/shifts') });

  const cellMap = useMemo(() => {
    const m = new Map<string, RosterData['entries'][number]>();
    for (const e of q.data?.entries ?? []) {
      const k = `${e.employeeId}|${e.date}`;
      const prev = m.get(k);
      if (!prev || e.kind !== 'shift') m.set(k, e);
    }
    return m;
  }, [q.data]);

  // Production has dozens of shifts per office: the legend shows those on screen, the picker those the office can use.
  const onScreen = useMemo(() => {
    const ids = new Set((q.data?.entries ?? []).map((e) => e.shiftId));
    return (shifts.data ?? []).filter((s) => ids.has(s.id));
  }, [q.data, shifts.data]);
  const usable = (shifts.data ?? []).filter((s) => !officeId || !s.officeId || s.officeId === officeId);

  const assign = useMutation({
    mutationFn: (v: { kind: 'shift' | 'day_off'; shiftId?: string }) => {
      const keys = [...selected].map((k) => k.split('|') as [string, string]);
      return post('/api/attendance/roster/assign', { employeeIds: [...new Set(keys.map((k) => k[0]))], dates: [...new Set(keys.map((k) => k[1]))], ...v });
    },
    onSuccess: () => {
      toast({ title: 'Roster updated', body: `${selected.size} cell${selected.size > 1 ? 's' : ''} changed. Timesheets re-derived.` });
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });

  const toggle = (key: string, e: React.MouseEvent) => {
    setSelected((prev) => {
      const next = new Set(e.metaKey || e.ctrlKey || e.shiftKey ? prev : []);
      if (e.shiftKey && anchor && q.data) {
        const [ae, ad] = anchor.split('|') as [string, string];
        const [be, bd] = key.split('|') as [string, string];
        const pi = q.data.people.map((p) => p.id);
        const [r0, r1] = [pi.indexOf(ae), pi.indexOf(be)].sort((a, b) => a - b) as [number, number];
        const [c0, c1] = [q.data.dates.indexOf(ad), q.data.dates.indexOf(bd)].sort((a, b) => a - b) as [number, number];
        for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) next.add(`${pi[r]}|${q.data.dates[c]}`);
      } else if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (!e.shiftKey) setAnchor(key);
  };

  const today = todayIn(me.employee?.timeZone ?? undefined);

  return (
    <div>
      <PageHead
        eyebrow={`Roster · ${date(start, { year: false })} – ${date(end)}`}
        title={
          <>
            Who works <em>when.</em>
          </>
        }
        lede="Click a cell to select it, shift-click to select a range, ⌘-click to add more. Then choose a shift or a day off."
        aside={
          <div className="row" style={{ ['--gap' as string]: '4px' }}>
            {me.access.shiftEdit ? (
              <Button variant="ghost" size="sm" icon="clock" onClick={() => setManaging(true)} style={{ marginRight: 8 }}>
                Shifts
              </Button>
            ) : null}
            <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous two weeks" onClick={() => setStart(addDays(start, -14))} />
            <Button size="sm" onClick={() => setStart(mondayOf(today))}>
              This week
            </Button>
            <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next two weeks" onClick={() => setStart(addDays(start, 14))} />
          </div>
        }
      />
      <div className="toolbar">
        <select className="select" style={{ width: 'auto' }} value={officeId} onChange={(e) => setOfficeId(e.target.value)} aria-label="Office">
          <option value="">All offices</option>
          {lookups.data?.offices.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <div className="row row--wrap" style={{ marginLeft: 'auto', ['--gap' as string]: '14px' }}>
          {onScreen.map((s) => (
            <span key={s.id} className="row faint" style={{ ['--gap' as string]: '6px', fontSize: 'var(--step--2)' }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
              <span className="mono">{s.code}</span> {s.startTime.slice(0, 5)}–{s.endTime.slice(0, 5)}
            </span>
          ))}
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton h={420} />
      ) : !q.data?.people.length ? (
        <Empty title="No one to roster" />
      ) : (
        <div className="roster" role="grid" aria-label="Roster" aria-multiselectable="true">
          <table>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', paddingLeft: 14, position: 'sticky', left: 0, zIndex: 3 }}>{q.data.people.length} people</th>
                {q.data.dates.map((d) => {
                  const dow = new Date(`${d}T00:00:00Z`).getUTCDay();
                  return (
                    <th key={d} data-weekend={dow === 0 || dow === 6} data-today={d === today}>
                      <div>{['S', 'M', 'T', 'W', 'T', 'F', 'S'][dow]}</div>
                      <div style={{ fontSize: 12 }}>{Number(d.slice(8))}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {q.data.people.map((p) => (
                <tr key={p.id}>
                  <th scope="row">
                    <div>{p.name}</div>
                    <div className="faint" style={{ fontSize: 10.5, fontWeight: 400 }}>
                      {p.jobPosition}
                    </div>
                  </th>
                  {q.data!.dates.map((d) => {
                    const key = `${p.id}|${d}`;
                    const e = cellMap.get(key);
                    const isSel = selected.has(key);
                    return (
                      <td key={d}>
                        <button type="button" className="roster-cell" aria-selected={isSel} onClick={(ev) => toggle(key, ev)} aria-label={`${p.name}, ${date(d)}: ${e?.kind === 'shift' ? e.code : (e?.kind ?? 'unscheduled').replace('_', ' ')}`}>
                          {e?.kind === 'shift' ? (
                            <span className="roster-cell__pill" style={{ background: `color-mix(in srgb, ${e.color ?? 'var(--ink-3)'} 22%, transparent)`, color: e.color ?? undefined }}>
                              {e.code}
                            </span>
                          ) : e?.kind === 'day_off' ? (
                            <span className="faint">·</span>
                          ) : e?.kind === 'leave' ? (
                            <span className="roster-cell__pill" style={{ background: 'var(--dusk-soft)', color: 'var(--dusk)' }}>
                              LV
                            </span>
                          ) : e?.kind === 'public_holiday' ? (
                            <span className="roster-cell__pill" style={{ background: 'var(--ochre-soft)', color: 'var(--ochre)' }}>
                              PH
                            </span>
                          ) : (
                            <span style={{ color: 'var(--ink-4)' }}>—</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <AnimatePresence>
        {selected.size && me.access.attendanceAdmin ? (
          <motion.div
            className="panel row row--wrap"
            style={{ position: 'fixed', zIndex: 40, bottom: 24, left: '50%', padding: '12px 14px', boxShadow: 'var(--shadow-pop)', ['--gap' as string]: '8px', maxWidth: 'calc(100vw - 32px)' }}
            initial={{ opacity: 0, y: 20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 16, x: '-50%' }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <span className="eyebrow" style={{ marginRight: 6 }}>
              {selected.size} selected
            </span>
            <select className="select" style={{ width: 'auto', maxWidth: 260, height: 32 }} value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Shift">
              <option value="">Choose a shift…</option>
              {usable.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} · {s.startTime.slice(0, 5)}–{s.endTime.slice(0, 5)} · {s.name}
                </option>
              ))}
            </select>
            <Button size="sm" variant="primary" disabled={!pick} loading={assign.isPending && assign.variables?.kind === 'shift'} onClick={() => assign.mutate({ kind: 'shift', shiftId: pick })}>
              Apply shift
            </Button>
            <Button size="sm" loading={assign.isPending && assign.variables?.kind === 'day_off'} onClick={() => assign.mutate({ kind: 'day_off' })}>
              Day off
            </Button>
            <Button size="sm" variant="ghost" icon="x" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </motion.div>
        ) : null}
      </AnimatePresence>
      <ShiftsDialog open={managing} onClose={() => setManaging(false)} shifts={shifts.data ?? []} today={today} />
    </div>
  );
}

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');
const hhmm = (t: string | null) => (t ? t.slice(0, 5) : '');

type Draft = { code: string; name: string; startTime: string; endTime: string; breakStart: string; breakEnd: string; color: string; officeId: string; crossDayClockIn: boolean; effectiveFrom: string };

/** Shift list and editor in one dialog (stacked dialogs would both close on Escape). */
function ShiftsDialog({ open, onClose, shifts, today }: { open: boolean; onClose: () => void; shifts: Shift[]; today: string }) {
  const lookups = useLookups();
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState<{ shift: Shift | null; f: Draft } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const close = () => {
    setEdit(null);
    setConfirming(null);
    onClose();
  };
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['shifts'] });
    void qc.invalidateQueries({ queryKey: ['roster'] });
  };
  const startEdit = (s: Shift | null) => {
    del.reset();
    save.reset();
    setConfirming(null);
    setEdit({
      shift: s,
      f: s
        ? { code: s.code, name: s.name, startTime: hhmm(s.startTime), endTime: hhmm(s.endTime), breakStart: hhmm(s.breakStart), breakEnd: hhmm(s.breakEnd), color: s.color, officeId: s.officeId ?? '', crossDayClockIn: s.crossDayClockIn, effectiveFrom: today }
        : { code: '', name: '', startTime: '08:00', endTime: '17:00', breakStart: '12:00', breakEnd: '13:00', color: '#8FA7D6', officeId: '', crossDayClockIn: false, effectiveFrom: today },
    });
  };

  const hoursChanged = (s: Shift, f: Draft) => f.startTime !== hhmm(s.startTime) || f.endTime !== hhmm(s.endTime) || f.breakStart !== hhmm(s.breakStart) || f.breakEnd !== hhmm(s.breakEnd) || f.crossDayClockIn !== s.crossDayClockIn;

  const save = useMutation({
    mutationFn: ({ shift, f }: { shift: Shift | null; f: Draft }): Promise<{ versioned?: boolean; effectiveFrom?: string | null; rebuilt?: number }> => {
      const b = { code: f.code.trim(), name: f.name.trim(), startTime: f.startTime, endTime: f.endTime, breakStart: f.breakStart || null, breakEnd: f.breakEnd || null, color: f.color, officeId: f.officeId || null, crossDayClockIn: f.crossDayClockIn };
      return shift ? patch(`/api/attendance/shifts/${shift.id}`, { ...b, ...(hoursChanged(shift, f) ? { effectiveFrom: f.effectiveFrom } : {}) }) : post('/api/attendance/shifts', b);
    },
    onSuccess: (res, v) => {
      toast({
        title: v.shift ? `${v.f.code} saved` : `${v.f.code} added`,
        body: res.effectiveFrom ? `New hours from ${date(res.effectiveFrom)}${res.versioned ? '; earlier days keep the old hours' : ''}. ${res.rebuilt ?? 0} timesheet days re-derived.` : undefined,
      });
      setEdit(null);
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: (s: Shift) => api(`/api/attendance/shifts/${s.id}`, { method: 'DELETE' }),
    onSuccess: (_r, s) => {
      toast({ title: `${s.code} deleted`, body: 'Past roster days keep it.' });
      refresh();
    },
    onSettled: () => setConfirming(null),
  });

  if (edit) {
    const { shift, f } = edit;
    const set = (patch_: Partial<Draft>) => setEdit({ shift, f: { ...f, ...patch_ } });
    const changed = shift ? hoursChanged(shift, f) : false;
    return (
      <Dialog
        open={open}
        onClose={close}
        eyebrow={shift ? `Shift · ${shift.code}` : 'New shift'}
        title={shift ? `Edit ${shift.name}` : 'Add a shift'}
        width={620}
        footer={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => setEdit(null)}>
              All shifts
            </Button>
            <span style={{ flex: 1 }} />
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(edit)}>
              {shift ? 'Save' : 'Add shift'}
            </Button>
          </>
        }
      >
        <div className="grid-12" style={{ gap: 14 }}>
          <div className="col-3"><Field label="Code">{(p) => <input {...p} className="input input--mono" maxLength={10} value={f.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} />}</Field></div>
          <div className="col-9"><Field label="Name">{(p) => <input {...p} className="input" value={f.name} onChange={(e) => set({ name: e.target.value })} />}</Field></div>
          <div className="col-3"><Field label="Starts">{(p) => <input {...p} type="time" className="input input--mono" value={f.startTime} onChange={(e) => set({ startTime: e.target.value })} />}</Field></div>
          <div className="col-3"><Field label="Ends" hint={f.startTime && f.endTime && f.startTime > f.endTime ? 'Next day (overnight)' : undefined}>{(p) => <input {...p} type="time" className="input input--mono" value={f.endTime} onChange={(e) => set({ endTime: e.target.value })} />}</Field></div>
          <div className="col-3"><Field label="Break from">{(p) => <input {...p} type="time" className="input input--mono" value={f.breakStart} onChange={(e) => set({ breakStart: e.target.value })} />}</Field></div>
          <div className="col-3"><Field label="Break to">{(p) => <input {...p} type="time" className="input input--mono" value={f.breakEnd} onChange={(e) => set({ breakEnd: e.target.value })} />}</Field></div>
          <div className="col-8">
            <Field label="Office" hint="Leave on all offices unless only one office works it">
              {(p) => (
                <select {...p} className="select" value={f.officeId} onChange={(e) => set({ officeId: e.target.value })}>
                  <option value="">All offices</option>
                  {lookups.data?.offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
            </Field>
          </div>
          <div className="col-4"><Field label="Colour">{(p) => <input {...p} type="color" className="input" style={{ padding: 4, height: 40 }} value={f.color} onChange={(e) => set({ color: e.target.value })} />}</Field></div>
          <div className="col-12"><Switch checked={f.crossDayClockIn} onChange={(v) => set({ crossDayClockIn: v })} label="Clock-in may fall on the previous day (early-morning starts)" showLabel /></div>
          {changed ? (
            <div className="col-12">
              <Field label="New hours apply from" hint="Days before this keep the old hours. It can’t fall inside a payroll period that is already approved.">
                {(p) => <input {...p} type="date" className="input input--mono" style={{ maxWidth: 220 }} value={f.effectiveFrom} onChange={(e) => set({ effectiveFrom: e.target.value })} />}
              </Field>
            </div>
          ) : null}
          {save.error ? <div className="col-12"><Callout tone="bad">{errText(save.error)}</Callout></div> : null}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      eyebrow={`${shifts.length} shifts`}
      title="Shifts"
      width={720}
      footer={
        <>
          <Button variant="ghost" onClick={close}>Close</Button>
          <Button variant="primary" icon="plus" onClick={() => startEdit(null)}>New shift</Button>
        </>
      }
    >
      <table className="table">
        <thead>
          <tr>
            <th>Shift</th>
            <th>Hours</th>
            <th>Break</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {shifts.map((s) => (
            <tr key={s.id}>
              <td>
                <div className="row" style={{ ['--gap' as string]: '10px' }}>
                  <span className="roster-cell__pill" style={{ background: `color-mix(in srgb, ${s.color} 22%, transparent)`, color: s.color }}>{s.code}</span>
                  <span>{s.name}</span>
                  {s.isDefault ? <Chip>Default</Chip> : null}
                </div>
              </td>
              <td className="mono">
                {hhmm(s.startTime)}–{hhmm(s.endTime)}
                {s.overnight ? <span className="faint"> +1</span> : null}
              </td>
              <td className="mono faint">{s.breakStart ? `${hhmm(s.breakStart)}–${hhmm(s.breakEnd)}` : '—'}</td>
              <td className="right">
                {s.isDefault ? (
                  <span className="faint" style={{ fontSize: 'var(--step--2)' }}>Covers unscheduled days</span>
                ) : confirming === s.id ? (
                  <div className="row" style={{ ['--gap' as string]: '4px', justifyContent: 'end' }}>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(null)}>Keep</Button>
                    <Button size="sm" variant="danger" loading={del.isPending} onClick={() => del.mutate(s)}>Delete {s.code}</Button>
                  </div>
                ) : (
                  <div className="row" style={{ ['--gap' as string]: '4px', justifyContent: 'end' }}>
                    <Button size="sm" variant="ghost" onClick={() => startEdit(s)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => { del.reset(); setConfirming(s.id); }}>Delete</Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {del.error ? <div style={{ marginTop: 14 }}><Callout tone="bad">{errText(del.error)}</Callout></div> : null}
    </Dialog>
  );
}
